<template>
  <div class="verify-shell">
    <div class="verify-card">
      <div v-if="state === 'loading'" class="verify-state">
        <div class="verify-spinner"></div>
        <p>Verifying your email…</p>
      </div>

      <div v-else-if="state === 'success'" class="verify-state">
        <div class="verify-icon success">✓</div>
        <h2>Email verified!</h2>
        <p>Your account is active. Taking you to the dashboard…</p>
      </div>

      <div v-else-if="state === 'expired'" class="verify-state">
        <div class="verify-icon warn">⚠</div>
        <h2>Link expired</h2>
        <p>This verification link has expired. Enter your email below to get a new one.</p>
        <input v-model="resendEmail" type="email" placeholder="you@example.com" class="verify-input" />
        <button class="verify-btn" :disabled="resending" @click="resend">
          {{ resending ? 'Sending…' : 'Resend verification email' }}
        </button>
        <p v-if="resendMessage" class="verify-message">{{ resendMessage }}</p>
      </div>

      <div v-else class="verify-state">
        <div class="verify-icon error">✕</div>
        <h2>Verification failed</h2>
        <p>{{ errorMessage }}</p>
        <a href="/login" class="verify-btn" style="text-align:center;">Back to login</a>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSitesStore } from '../stores/sites'
import api from '../utils/api'

export default {
  name: 'VerifyEmail',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const sitesStore = useSitesStore()

    const state = ref('loading')
    const errorMessage = ref('')
    const resendEmail = ref('')
    const resending = ref(false)
    const resendMessage = ref('')

    onMounted(async () => {
      const token = route.query.token
      if (!token) {
        errorMessage.value = 'No verification token found in the link.'
        state.value = 'error'
        return
      }

      try {
        const response = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
        const data = response.data

        localStorage.setItem('bwai_token', data.token)
        localStorage.setItem('bwai_user', JSON.stringify(data.user))
        localStorage.setItem('bwai_tenant', JSON.stringify(data.tenant))
        api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`
        sitesStore.setUser(data.user, data.tenant)

        state.value = 'success'
        setTimeout(() => router.push('/sites'), 1500)
      } catch (err) {
        const data = err.response?.data
        if (data?.expired) {
          resendEmail.value = data.email || ''
          state.value = 'expired'
        } else {
          errorMessage.value = data?.error || 'The verification link is invalid or has already been used.'
          state.value = 'error'
        }
      }
    })

    const resend = async () => {
      resending.value = true
      resendMessage.value = ''
      try {
        await api.post('/auth/resend-verification', { email: resendEmail.value })
        resendMessage.value = 'Sent! Check your inbox.'
      } catch {
        resendMessage.value = 'Failed to send. Please try again.'
      } finally {
        resending.value = false
      }
    }

    return { state, errorMessage, resendEmail, resending, resendMessage, resend }
  }
}
</script>

<style scoped>
.verify-shell {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f0f1a;
}
.verify-card {
  background: rgba(255,255,255,.05);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 16px;
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
  text-align: center;
  color: #e2e2f0;
}
.verify-state { display: flex; flex-direction: column; align-items: center; gap: 12px; }
.verify-icon {
  width: 56px; height: 56px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 24px; font-weight: 700;
}
.verify-icon.success { background: rgba(52,211,153,.15); color: #34d399; }
.verify-icon.warn    { background: rgba(251,191,36,.15);  color: #fbbf24; }
.verify-icon.error   { background: rgba(248,113,113,.15); color: #f87171; }
.verify-spinner {
  width: 40px; height: 40px; border-radius: 50%;
  border: 3px solid rgba(109,93,255,.2);
  border-top-color: #6d5dff;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
h2 { margin: 0; font-size: 20px; font-weight: 700; }
p  { margin: 0; font-size: 14px; color: rgba(226,226,240,.7); line-height: 1.6; }
.verify-input {
  width: 100%; padding: 10px 14px; border-radius: 8px;
  background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.15);
  color: #e2e2f0; font-size: 14px; outline: none; box-sizing: border-box;
}
.verify-btn {
  display: block; width: 100%; padding: 11px;
  background: #6d5dff; color: #fff; border: none;
  border-radius: 8px; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: opacity .15s; text-decoration: none;
}
.verify-btn:disabled { opacity: .5; cursor: not-allowed; }
.verify-message { font-size: 13px; color: #34d399; margin: 0; }
</style>
